<script setup lang="ts">
import type { Editor as TipTapEditor } from '@tiptap/core';
import { Placeholder } from '@tiptap/extensions';
import Mathematics from '@tiptap/extension-mathematics';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import { BubbleMenu } from '@tiptap/vue-3/menus';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { $t } from '@teachino/shared-i18n';
import 'katex/dist/katex.min.css';
import {
  generateId,
  prepareRichTextHtmlForDisplay,
} from '@teachino/shared-util';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import BubbleMenuTooltipButton from '../BubbleMenuTooltipButton/BubbleMenuTooltipButton.vue';
import { useKeyboardShortcut } from '../KeyboardShortcut/useKeyboardShortcut';
import { FLOATING_OVERLAY_Z_INDEX } from '../layering/layering';
import type { RichTextProps } from './rich-text';

const bubbleMenuPluginKey = `rich-text-bubble-menu-${generateId()}`;

const appendBubbleMenuTo = (): HTMLElement => document.body;

const bubbleMenuElement = ref<HTMLElement | null>(null);

const props = withDefaults(defineProps<RichTextProps>(), {
  modelValue: '',
  readonly: false,
  autofocus: false,
  placeholder: undefined,
  openFormulaDialog: undefined,
  enableBoldItalicUnderline: true,
  enableLists: true,
  multiline: true,
});

const { kbd } = useKeyboardShortcut();

const editorFrameStyle = computed(() => {
  if (!props.editorHeight) {
    return undefined;
  }

  return {
    height: props.editorHeight,
    minHeight: props.editorHeight,
    maxHeight: props.editorHeight,
    overflowY: 'auto' as const,
  };
});

const hasFixedEditorHeight = computed(() => props.editorHeight != null);

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'update:modelValue': [value: string];
}>();

const placeholderExtensions = props.placeholder
  ? [Placeholder.configure({ placeholder: props.placeholder })]
  : [];

const editor = new Editor({
  extensions: [
    ...placeholderExtensions,
    StarterKit.configure({
      heading: false,
      blockquote: false,
      code: false,
      codeBlock: false,
      horizontalRule: false,
      strike: false,
      trailingNode: false,
      bold: props.enableBoldItalicUnderline ? {} : false,
      italic: props.enableBoldItalicUnderline ? {} : false,
      underline: props.enableBoldItalicUnderline ? {} : false,
      bulletList: props.enableLists ? {} : false,
      orderedList: props.enableLists ? {} : false,
      listItem: props.enableLists ? {} : false,
    }),
    Mathematics.configure({
      inlineOptions: {
        onClick: (node, pos) => {
          if (props.readonly || !props.openFormulaDialog) {
            return;
          }

          void updateFormulaAtPosition(node, pos);
        },
      },
      katexOptions: {
        throwOnError: false,
        errorColor: '#FF0000',
      },
    }),
  ],
  editable: !props.readonly,
  editorProps: {
    attributes: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'aria-label': props.label,
      class: 'tiptap rich-text-content',
    },
    handleKeyDown: (_view, event) => {
      if (!props.multiline && event.key === 'Enter') {
        event.preventDefault();
        return true;
      }

      return false;
    },
    handlePaste: (_view, event) => {
      if (!props.multiline) {
        const text = event.clipboardData?.getData('text/plain');

        if (text?.includes('\n')) {
          event.preventDefault();
          editor
            .chain()
            .focus()
            .insertContent(text.replace(/\r?\n/g, ' '))
            .run();
          return true;
        }
      }

      return false;
    },
  },
  onCreate: ({ editor: createdEditor }) => {
    migrateMathExpressions(createdEditor);
    createdEditor.on('blur', handleEditorBlur);
  },
  onUpdate: ({ editor: updatedEditor }) => {
    // Content may store formulas as plain $$...$$ text. Migration converts
    // them to inline-math nodes and triggers another onUpdate — skip emitting
    // that intermediate step so v-model only gets the final HTML.
    const didMigrateExpressions = migrateMathExpressions(updatedEditor);
    if (didMigrateExpressions) {
      return;
    }

    emit('update:modelValue', updatedEditor.getHTML());
  },
});

watch(
  () => props.modelValue,
  (value) => {
    const nextValue = prepareRichTextHtmlForDisplay(value);

    if (editor.getHTML() === nextValue) {
      return;
    }

    editor.commands.setContent(nextValue, { emitUpdate: false });
    migrateMathExpressions(editor);
  },
  { immediate: true },
);

watch(
  () => props.readonly,
  (readonly) => {
    editor.setEditable(!readonly, false);
  },
  { immediate: true },
);

watch(
  () => props.autofocus,
  (autofocus) => {
    if (autofocus && !props.readonly) {
      editor.commands.focus('end');
    }
  },
  { immediate: true },
);

const toggleBold = () => {
  editor.chain().focus().toggleBold().run();
};

const toggleItalic = () => {
  editor.chain().focus().toggleItalic().run();
};

const toggleUnderline = () => {
  editor.chain().focus().toggleUnderline().run();
};

const toggleBulletList = () => {
  editor.chain().focus().toggleBulletList().run();
};

const toggleOrderedList = () => {
  editor.chain().focus().toggleOrderedList().run();
};

const insertFormulaFromSelection = () => {
  if (props.readonly || !props.openFormulaDialog) {
    return;
  }

  const { from, to } = editor.state.selection;
  void insertFormula({ from, to });
};

const hasBubbleMenuActions = computed(
  () =>
    props.enableBoldItalicUnderline ||
    props.enableLists ||
    !!props.openFormulaDialog,
);

const isBubbleMenuVisible = ({
  editor: bubbleMenuEditor,
  view,
  element,
  from,
  to,
}: {
  editor: TipTapEditor;
  view: EditorView;
  element: HTMLElement;
  from: number;
  to: number;
}): boolean => {
  bubbleMenuElement.value = element;

  if (
    props.readonly ||
    !bubbleMenuEditor.isEditable ||
    !hasBubbleMenuActions.value
  ) {
    return false;
  }

  const isChildOfMenu = element.contains(document.activeElement);
  const hasEditorFocus = view.hasFocus() || isChildOfMenu;

  if (!hasEditorFocus || from === to) {
    return false;
  }

  return true;
};

function handleEditorBlur({ event }: { event: FocusEvent }): void {
  const menuElement = bubbleMenuElement.value;

  if (
    event.relatedTarget instanceof Node &&
    menuElement?.contains(event.relatedTarget)
  ) {
    return;
  }

  const { from, to } = editor.state.selection;
  if (from === to) {
    return;
  }

  editor.commands.setTextSelection(from);
}

function migrateMathExpressions(currentEditor: TipTapEditor): boolean {
  const transaction = createMathMigrateTransaction(
    currentEditor,
    currentEditor.state.tr,
  );

  if (transaction.steps.length === 0) {
    return false;
  }

  currentEditor.view.dispatch(transaction);
  return true;
}

function createMathMigrateTransaction(
  editorInstance: TipTapEditor,
  tr: Transaction,
) {
  tr.doc.descendants((node, pos) => {
    if (!node.isText || !node.text?.includes('$$')) {
      return;
    }

    const { text } = node;
    const matches = text.matchAll(/\$\$(?!\d+\$)(.+?)\$\$(?!\d)/g);

    for (const match of matches) {
      const mathMatch = match[0];
      if (!mathMatch) {
        continue;
      }

      const start = match.index;
      const end = start + mathMatch.length;

      const { inlineMath } = editorInstance.schema.nodes;

      tr.replaceWith(
        tr.mapping.map(pos + start),
        tr.mapping.map(pos + end),
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        inlineMath.create({ latex: mathMatch.slice(2, -2) }),
      );
    }
  });

  tr.setMeta('addToHistory', false);
  return tr;
}

async function insertFormula(selection: { from: number; to: number }) {
  if (!props.openFormulaDialog) {
    return;
  }

  const latex = (await props.openFormulaDialog()).trim();
  if (!latex) {
    return;
  }

  editor
    .chain()
    .focus()
    .setTextSelection(selection)
    .deleteSelection()
    .insertInlineMath({ latex })
    .run();
}

async function updateFormulaAtPosition(node: ProseMirrorNode, pos: number) {
  if (!props.openFormulaDialog) {
    return;
  }

  const currentFormula =
    typeof node.attrs.latex === 'string' ? node.attrs.latex : undefined;
  const latex = (await props.openFormulaDialog(currentFormula)).trim();
  if (!latex) {
    return;
  }

  editor
    .chain()
    .focus()
    .setNodeSelection(pos)
    .updateInlineMath({ latex })
    .focus(pos + 1)
    .run();
}

onBeforeUnmount(() => {
  editor.off('blur', handleEditorBlur);
  editor.destroy();
});
</script>

<template>
  <div
    class="t-rich-text min-w-0"
    :class="{
      't-rich-text--fixed-height': hasFixedEditorHeight,
      't-rich-text--editable': !props.readonly,
    }"
  >
    <BubbleMenu
      v-if="!props.readonly && hasBubbleMenuActions"
      class="rich-text-bubble-menu-host"
      :style="{ zIndex: FLOATING_OVERLAY_Z_INDEX }"
      :editor="editor"
      :plugin-key="bubbleMenuPluginKey"
      :append-to="appendBubbleMenuTo"
      :options="{ strategy: 'fixed' }"
      :should-show="isBubbleMenuVisible"
    >
      <div
        class="flex shrink-0 flex-nowrap items-center gap-1 rounded-md border border-neutral-200 bg-white p-1 shadow-md"
      >
        <template v-if="props.enableBoldItalicUnderline">
          <BubbleMenuTooltipButton
            icon="text_bold"
            :description="$t('editor.tools.bold.name')"
            :keyboard-shortcut="kbd().mod().key('b')"
            :is-active="editor.isActive('bold')"
            @click="toggleBold"
          />
          <BubbleMenuTooltipButton
            icon="text_italic"
            :description="$t('editor.tools.italic.name')"
            :keyboard-shortcut="kbd().mod().key('i')"
            :is-active="editor.isActive('italic')"
            @click="toggleItalic"
          />
          <BubbleMenuTooltipButton
            icon="text_underline"
            :description="$t('editor.tools.underline.name')"
            :keyboard-shortcut="kbd().mod().key('u')"
            :is-active="editor.isActive('underline')"
            @click="toggleUnderline"
          />
        </template>
        <template v-if="props.enableLists">
          <BubbleMenuTooltipButton
            icon="list_bullet"
            :description="$t('editor.tools.list.bulleted_list.name')"
            :is-active="editor.isActive('bulletList')"
            @click="toggleBulletList"
          />
          <BubbleMenuTooltipButton
            icon="list_number"
            :description="$t('editor.tools.list.ordered_list.name')"
            :is-active="editor.isActive('orderedList')"
            @click="toggleOrderedList"
          />
        </template>
        <BubbleMenuTooltipButton
          v-if="props.openFormulaDialog"
          icon="function_square"
          :description="$t('katex.enter_equation')"
          @click="insertFormulaFromSelection"
        />
      </div>
    </BubbleMenu>

    <div
      class="rich-text-editor-frame rounded-md"
      :class="
        props.readonly
          ? 'bg-transparent'
          : 'focus-within:border-primary-500 focus-within:ring-primary-200 border border-neutral-300 bg-white focus-within:ring-2'
      "
      :style="editorFrameStyle"
    >
      <EditorContent :editor="editor" />
    </div>
  </div>
</template>

<style scoped>
.t-rich-text :deep(.rich-text-content) {
  min-height: 1.5rem;
  outline: none;
}

.t-rich-text--editable .rich-text-editor-frame :deep(.rich-text-content) {
  padding: 0.5rem 0.75rem;
}

.t-rich-text--fixed-height .rich-text-editor-frame :deep(> div) {
  height: 100%;
}

.t-rich-text--fixed-height .rich-text-editor-frame :deep(.rich-text-content) {
  box-sizing: border-box;
  min-height: 100%;
}

/* Render the placeholder out of flow (`position: absolute`) so the text caret
   sits at the START of the empty line like a native input, instead of after the
   placeholder text. `max-width: 100%` keeps a long placeholder wrapped inside
   the editor box so it can't paint over neighbouring UI on iOS Safari — which
   is why the old `float: left; height: 0` approach was removed. */
.t-rich-text :deep(.rich-text-content p.is-editor-empty:first-child) {
  position: relative;
}
.t-rich-text :deep(.rich-text-content p.is-editor-empty:first-child::before) {
  position: absolute;
  inset-inline-start: 0;
  top: 0;
  max-width: 100%;
  color: var(--color-neutral-500);
  content: attr(data-placeholder);
  pointer-events: none;
}

.t-rich-text :deep(.rich-text-content ol),
.t-rich-text :deep(.rich-text-content ul) {
  padding-inline-start: 1.25rem;
}

.t-rich-text :deep(.rich-text-content li) {
  margin-left: 0;
}
</style>

<!-- TipTap re-parents the bubble menu to document.body -->
<style>
.rich-text-bubble-menu-host {
  z-index: 101;
}
</style>
